/**
 * The three knobs that separate the low-end alignment path from today's path,
 * plus the two loop-pacing values. BUG-0008.
 *
 * THE INVARIANT THIS FILE EXISTS TO HOLD: on `standard` (and on `unknown`, the
 * fail-safe) every value is the literal that is in `PoseDetectionManager`
 * today — 0 latch, 0 inflation, stride 4, 15 Hz, no yield.
 * `PoseDetectionManager` reads these instead of its own literals, so "the
 * shared path is unchanged" is a property of the NUMBERS rather than of a
 * branch somebody has to keep honest.
 *
 * `unknown` maps to the standard path deliberately, and never to `assisted`.
 * An unclassified device on today's behaviour carries a KNOWN risk (it may hit
 * BUG-0008); an unclassified device on a looser gate carries an unknown
 * avatar-quality risk that surfaces as a wrong avatar rather than as an error —
 * the backend has no runtime pose validator on the PWA path.
 *
 * See docs/superpowers/plans/2026-09-02-bug-0008-low-end-device-alignment-fix-plan.md
 * §1.2 (why the standard path is provably inert) and §3 (what each knob buys).
 */

export type DeviceClass = "standard" | "assisted" | "marginal" | "unknown";

export interface AlignmentTuning {
  /** How long an in-area observation stays valid. 0 = today's same-emit gate. */
  jointLatchMs: number;
  /** Outward/upward inflation of each target box, as a fraction of its own size. */
  boxInflateRatio: number;
  /** Run the main-thread EfficientDet pass every Nth processed frame. */
  objectDetectionStride: number;
  /** Throttle target for `processFrame`. */
  targetHz: number;
  /** Delay before scheduling the next detection loop iteration. 0 = plain rAF. */
  loopYieldMs: number;
}

export const STANDARD_TUNING: AlignmentTuning = {
  jointLatchMs: 0,
  boxInflateRatio: 0,
  objectDetectionStride: 4,
  targetHz: 15,
  loopYieldMs: 0,
};

/**
 * Two consecutive emits at 3 Hz, three at 5 Hz — enough for four joints
 * measured across three consecutive ticks to satisfy the gate.
 *
 * Ceiling is 1000ms, and the reason it can be this generous is what happens
 * AFTER the gate: `ScanStatus` holds `correct-posture` for 2s, `hold-up-arms`
 * for 5s and `ready-steady-spin` for 2.655s before the first tick — 9.65s
 * during which the screen says "Hold arms up and get ready to spin 1x in
 * place". A 600ms memory of a joint position is small against that.
 */
const LOW_END_LATCH_MS = 600;

/**
 * The latch duration the SHADOW counter measures against — see
 * `latchRescuedEmits` in `PoseDetectionManager`.
 *
 * Nothing applies this value to the gate. It exists so that "would a per-joint
 * latch actually convert any emits on real traffic?" can be answered from
 * production BEFORE the latch is shipped to anybody, which is the one question
 * about the BUG-0008 fix that no passive measurement could otherwise reach.
 * Deliberately the same number the low-end path would use: an answer measured
 * against a different one would not transfer.
 */
export const SHADOW_LATCH_MS = LOW_END_LATCH_MS;

/**
 * Ceiling is 0.20: above that the hand/leg PNGs `ScanCanvas` stretches to the
 * box dimensions visibly distort, and the wrist boxes stop reading as "hands
 * out to the side". Do not raise this without device testing.
 */
const LOW_END_BOX_INFLATE = 0.12;

/**
 * Honest accounting: INERT for the target population. A device running at
 * 2.9-8 Hz is already slower than 15 Hz, so the throttle's leading edge fires
 * on every call and suppresses nothing — and the detection loop in
 * `useDetectionEffects` awaits each `processFrame` before requesting the next
 * animation frame, so it never re-enters either. Kept because it makes the
 * intended cadence explicit and testable, and because it DOES bind if a device
 * recovers thermally mid-session.
 */
const LOW_END_TARGET_HZ = 8;

/**
 * The one that is actually felt. On a device where the loop body takes 300ms,
 * back-to-back rAF means the main thread is never idle: the canvas repaint,
 * the guidance text and the box redraw all queue behind the next detect.
 */
const LOW_END_LOOP_YIELD_MS = 20;

/**
 * `heightRatio` drives "Move Back" / "Move Closer" and refreshes only on the
 * strided EfficientDet pass, so a wide stride on a slow device makes guidance
 * unresponsive. Cap the staleness rather than the stride.
 */
const HEIGHT_RATIO_MAX_STALENESS_MS = 1500;
const MIN_STRIDE = 4;
const MAX_STRIDE = 12;

/**
 * @param cycleMs measured detect-cycle time (p50). Used only to size the
 *   object-detection stride; a non-positive value falls back to the most
 *   conservative stride.
 */
export function tuningFor(
  deviceClass: DeviceClass,
  cycleMs: number
): AlignmentTuning {
  if (deviceClass !== "assisted" && deviceClass !== "marginal") {
    return STANDARD_TUNING;
  }
  const safeCycleMs = cycleMs > 0 ? cycleMs : HEIGHT_RATIO_MAX_STALENESS_MS;
  const stride = Math.round(HEIGHT_RATIO_MAX_STALENESS_MS / safeCycleMs);
  return {
    jointLatchMs: LOW_END_LATCH_MS,
    boxInflateRatio: LOW_END_BOX_INFLATE,
    objectDetectionStride: Math.min(MAX_STRIDE, Math.max(MIN_STRIDE, stride)),
    targetHz: LOW_END_TARGET_HZ,
    loopYieldMs: LOW_END_LOOP_YIELD_MS,
  };
}
